import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import heroImage from "@/assets/service-yachts.webp";
import yachtImage from "@/assets/yacht.webp";
import missionImage from "@/assets/mission-yacht.webp";
import logoYachts from "@/assets/logo-yachts-hero.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const whyReasons = [
  {
    title: "Curated fleet",
    text: "We work with a carefully selected range of yachts, chosen for quality, design, and overall experience.",
  },
  {
    title: "Seamless arrangements",
    text: "From initial request to departure, every detail is handled with precision.",
  },
  {
    title: "Access to prime routes",
    text: "We arrange routes to the most sought-after locations, from hidden coves to iconic beach clubs.",
  },
  {
    title: "Discretion and service",
    text: "Every charter is handled with care, ensuring privacy and a consistent level of service.",
  },
  {
    title: "Personal approach",
    text: "Each booking is tailored individually, aligned with your preferences and schedule.",
  },
];

const steps = [
  { step: "1", title: "Request", text: "Share your preferred yacht, destination, and dates." },
  { step: "2", title: "Selection", text: "We present available options aligned with your preferences." },
  { step: "3", title: "Confirmation", text: "Once confirmed, the yacht and crew are secured." },
  { step: "4", title: "Departure", text: "Board your yacht and enjoy the experience." },
  { step: "5", title: "Support", text: "Our team remains available throughout your charter." },
];

const destinations = [
  { label: "Ibiza", path: "/yachts/ibiza" },
  { label: "Marbella", path: "/yachts/marbella" },
  { label: "Saint-Tropez", path: "/saint-tropez#services" },
  { label: "Dubai", path: "/dubai#services" },
];

const destDescriptions = [
  {
    label: "Ibiza",
    text: "Ibiza offers one of the most iconic yachting experiences in the Mediterranean, where clear turquoise waters meet hidden coves and open horizons. Cruising between Ibiza and Formentera, the pace of the island shifts — from vibrant energy to complete stillness within moments.\n\nDays are spent anchored in secluded bays, swimming in calm waters, or arriving by sea at some of the island's most sought-after beach clubs. The experience is defined by freedom, movement, and the ability to shape your day without limitation.",
  },
  {
    label: "Saint-Tropez",
    text: "Saint-Tropez represents a timeless Riviera lifestyle, where yachting is part of the daily rhythm. From the moment you enter its harbour, the atmosphere reflects understated glamour and refined coastal living.\n\nCruising along the Côte d'Azur reveals a sequence of elegant destinations — from quiet anchor points to renowned beach clubs such as Pampelonne. Whether for long lunches by the sea or afternoons spent offshore, each moment carries a sense of ease and sophistication.",
  },
  {
    label: "Dubai",
    text: "Dubai offers a modern and distinctive yachting experience, where the city's skyline meets open water. From the marina, the coastline unfolds into a series of architectural landmarks, best experienced from the sea.\n\nCruising past Palm Jumeirah, Burj Al Arab, and the city's coastline provides a unique perspective — combining privacy on the water with the energy of the city in the background. It is an experience defined by contrast, space, and contemporary luxury.",
  },
  {
    label: "Marbella",
    text: "Marbella offers a more relaxed and understated approach to yachting, defined by calm waters and a slower pace along the Costa del Sol. The coastline stretches between elegant marinas and open sea, creating an environment focused on comfort and ease.\n\nDays are shaped by sun, space, and simplicity — whether cruising along the coast, anchoring offshore, or enjoying the quiet rhythm of the Mediterranean. It is a destination where yachting feels effortless and unhurried.",
  },
];

const faqs = [
  { q: "How does booking a yacht through Arzani work?", a: "After submitting your request with your preferred destination, dates, and requirements, we present a curated selection of available yachts. Once you confirm, we handle all arrangements including crew, route planning, and onboard services." },
  { q: "What types of yachts are available?", a: "We offer a curated range from elegant open yachts and day cruisers to performance boats and superyachts, selected for quality, comfort, and design." },
  { q: "Can I request a specific yacht?", a: "Yes. If you have a particular yacht in mind, we will check availability and arrange it for you." },
  { q: "Is a crew included?", a: "Yes. All charters include a professional crew dedicated to your comfort and safety throughout the journey." },
  { q: "Can I customize the route?", a: "Absolutely. Routes are tailored to your preferences, whether you prefer hidden coves, beach clubs, or open water." },
  { q: "Is a deposit required?", a: "Yes. A deposit is required to confirm the reservation. Full details are provided during the booking process." },
  { q: "Are food and drinks included?", a: "Onboard catering can be arranged as part of the charter. We coordinate dining preferences and provisions in advance." },
  { q: "What happens in case of bad weather?", a: "Your captain will assess conditions and suggest alternative routes or rescheduling options. Safety is always the priority." },
  { q: "What happens in case of cancellation?", a: "Cancellation policies vary depending on the yacht and will be communicated clearly before confirmation." },
  { q: "Can yachts be arranged last-minute?", a: "Yes, subject to availability. Our team will always aim to accommodate urgent requests." },
];

const YachtsBrand = () => {
  const navigate = useNavigate();

  const handleDestinationClick = (dest: typeof destinations[0]) => {
    if (dest.path) {
      navigate(dest.path);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Yachts — Private Charters Across Our Destinations"
        description="Curated private yacht charters in Ibiza, Marbella, Saint-Tropez and beyond — arranged with discretion and precision."
        path="/yachts"
      />
      <Navigation />

      {/* Hero — cinematic full-bleed */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Yachts" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Private Charters · Worldwide
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Yachts
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Time on the water changes the pace of everything.
          </motion.p>
        </div>
      </section>

      {/* Opening Text — editorial centered */}
      <section className="py-28 md:py-40 bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.span
            className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, ease }}
          >
            The Experience
          </motion.span>
          <motion.p
            className="font-accent text-foreground/80 text-xl md:text-2xl lg:text-3xl leading-[1.6] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, delay: 0.2, ease }}
          >
            Each charter is carefully arranged to offer privacy, freedom, and a refined experience at sea.
          </motion.p>
          <motion.div
            className="w-10 h-px bg-bronze/40 mx-auto my-10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.4, ease }}
          />
          <motion.p
            className="font-body text-muted-foreground text-sm md:text-base leading-[2]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, delay: 0.5, ease }}
          >
            From iconic open yachts to elegant cruisers, every vessel is selected to deliver comfort, performance, and effortless enjoyment. Whether for a day along the coastline or a longer escape between destinations, each journey is defined by simplicity, discretion, and complete attention to detail.
          </motion.p>
        </div>
      </section>


      {/* Destinations Bar */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block text-center mb-10">
              Our Destinations
            </span>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {destinations.map((dest) => (
                <button
                  key={dest.label}
                  onClick={() => handleDestinationClick(dest)}
                  disabled={!dest.path}
                  className={`px-6 md:px-10 py-3 text-[10px] md:text-xs tracking-[0.25em] uppercase font-label border transition-all duration-700 ${
                    dest.path
                      ? "border-foreground/20 text-foreground hover:bg-foreground hover:text-background cursor-pointer"
                      : "border-foreground/8 text-muted-foreground/30 cursor-default"
                  }`}
                >
                  {dest.label}
                  {!dest.path && <span className="ml-2 text-[8px] tracking-normal normal-case opacity-50">Soon</span>}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Destination Descriptions — asymmetric editorial grid */}
      <section className="pb-24 md:pb-36 bg-background">
        <div className="container-luxury max-w-6xl">
          {destDescriptions.map((dest, i) => (
            <motion.div
              key={dest.label}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-20 md:mb-28 last:mb-0 items-start ${
                i % 2 === 1 ? "md:direction-rtl" : ""
              }`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <div className={`md:col-span-3 ${i % 2 === 1 ? "md:col-start-10" : ""}`}>
                <span className="font-label text-bronze text-[9px] tracking-[0.3em] block mb-4">0{i + 1}</span>
                <h3 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase leading-[1]">
                  {dest.label}
                </h3>
              </div>
              <div className={`md:col-span-7 ${i % 2 === 1 ? "md:col-start-1 md:row-start-1" : "md:col-start-5"}`}>
                {dest.text.split("\n\n").map((para, j) => (
                  <p key={j} className="font-body text-muted-foreground text-sm leading-[2] mb-5 last:mb-0">
                    {para}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Editorial Quote */}
      <section className="py-24 md:py-36 bg-warm-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="w-full h-full" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary-foreground)) 1px, transparent 0)", backgroundSize: "48px 48px" }} />
        </div>
        <div className="container-luxury max-w-3xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.8, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-10">A Perspective</span>
            <p className="font-accent text-primary-foreground/70 text-xl md:text-2xl lg:text-[28px] leading-[1.7] italic">
              "The best moments at sea are not the ones you plan — they are the ones that unfold when everything else has been taken care of."
            </p>
            <div className="w-10 h-px bg-bronze/30 mx-auto mt-10" />
          </motion.div>
        </div>
      </section>

      {/* Your Charter — asymmetric 7/5 layout */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-center">
            <motion.div
              className="md:col-span-7 order-2 md:order-1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.8, ease }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <motion.img
                  src={yachtImage}
                  alt="Yacht charter"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  whileInView={{ scale: [1.05, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease }}
                />
              </div>
            </motion.div>
            <motion.div
              className="md:col-span-5 order-1 md:order-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.2, ease }}
            >
              <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">Your Charter</span>
              <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-wide uppercase leading-[1.1] mb-8">
                Tailored to<br />Your Preferences
              </h2>
              <div className="w-8 h-px bg-bronze/40 mb-8" />
              <p className="font-body text-muted-foreground text-sm leading-[2] mb-6">
                Every charter is tailored to your preferences, from route planning and onboard services to dining reservations and anchor locations. Whether cruising towards hidden coves, beach clubs, or open waters, each detail is arranged in advance to create a seamless experience from departure to return.
              </p>
              <p className="font-body text-muted-foreground text-sm leading-[2]">
                With a professional crew dedicated to your comfort, your time on board remains uninterrupted — allowing you to experience the destination from a different perspective.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Book with Arzani — refined with numbering */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-5xl">
          <motion.div
            className="text-center mb-20 md:mb-28"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">Why Book With Arzani</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              The Difference
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 md:gap-x-16">
            {whyReasons.map((reason, i) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: i * 0.08, ease }}
              >
                <span className="font-display text-4xl text-primary-foreground/8 block mb-3">0{i + 1}</span>
                <h3 className="font-display-medium text-sm text-primary-foreground tracking-[0.2em] uppercase mb-4">
                  {reason.title}
                </h3>
                <div className="w-6 h-px bg-bronze/40 mb-4" />
                <p className="font-body text-primary-foreground/45 text-sm leading-[2]">
                  {reason.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works — minimal horizontal steps */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury max-w-5xl">
          <motion.div
            className="text-center mb-20 md:mb-28"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Your Booking
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
            {steps.map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <span className="font-display text-4xl md:text-5xl text-foreground/6 block mb-3">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-xs text-foreground tracking-[0.2em] uppercase mb-3">
                  {item.title}
                </h3>
                <div className="w-5 h-px bg-bronze/30 mx-auto mb-3" />
                <p className="font-body text-muted-foreground text-xs leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">FAQ</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          {(() => {
            const left = faqs.slice(0, 5);
            const right = faqs.slice(5);
            return (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease }}
              >
                <Accordion type="single" collapsible className="w-full">
                  {left.map((item, i) => (
                    <AccordionItem key={i} value={`faq-left-${i}`} className="border-primary-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-primary-foreground/80 tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-primary-foreground/45 text-sm leading-[1.9]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <Accordion type="single" collapsible className="w-full">
                  {right.map((item, i) => (
                    <AccordionItem key={i} value={`faq-right-${i}`} className="border-primary-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-primary-foreground/80 tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-primary-foreground/45 text-sm leading-[1.9]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            );
          })()}

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <p className="font-body text-primary-foreground/30 text-sm mb-6">Still have a question?</p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-primary-foreground/15 text-primary-foreground/70 text-xs tracking-[0.25em] uppercase font-label hover:bg-primary-foreground/5 transition-colors duration-700"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA — atmospheric */}
      <section className="relative py-32 md:py-44 overflow-hidden">
        <div className="absolute inset-0">
          <img src={missionImage} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-foreground/75" />
        </div>
        <div className="container-luxury max-w-2xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.35em] block mb-8">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-primary-foreground tracking-wide uppercase mb-6 leading-[1.1]">
              Request a Charter
            </h2>
            <p className="font-body text-primary-foreground/45 text-sm leading-[2] mb-12 max-w-lg mx-auto">
              Share your preferred destination, dates, and any special requirements.
              Our team will respond promptly with a tailored selection.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-12 py-3.5 border border-primary-foreground/20 text-primary-foreground text-xs tracking-[0.25em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-700"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-12 py-3.5 border border-primary-foreground/20 text-primary-foreground text-xs tracking-[0.25em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-700"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-primary-foreground/20 text-xs mt-10 tracking-wide">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YachtsBrand;
